import React, { useEffect, useState, useRef } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  Button,
  CircularProgress,
  makeStyles,
  ClickAwayListener,
} from '@material-ui/core';
import NotificationsOutlinedIcon from '@material-ui/icons/NotificationsOutlined';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import InfoIcon from '@material-ui/icons/Info';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';
import ErrorIcon from '@material-ui/icons/Error';
import DeleteIcon from '@material-ui/icons/Delete';
import { useNotificationService } from 'data/services';
import { InAppNotificationDto } from 'data/dtos';
import { createNotification } from 'factories/notificationFactory';
import { useIntl } from 'react-intl';

const POLL_INTERVAL = 60000;

const useStyles = makeStyles((theme) => ({
  badge: {
    backgroundColor: '#ff5252',
    color: '#fff',
  },
  popover: {
    pointerEvents: 'none',
  },
  paper: {
    pointerEvents: 'auto',
    minWidth: 380,
    maxWidth: 500,
    maxHeight: 600,
    borderRadius: 8,
    boxShadow: '0 5px 40px rgba(0,0,0,0.16)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: '16px',
  },
  markAllBtn: {
    textTransform: 'none',
    fontSize: '12px',
    color: theme.palette.primary.main,
  },
  content: {
    maxHeight: 'calc(400px - 60px)',
    overflowY: 'auto',
    padding: '8px 0',
  },
  viewAllButton: {
    padding: '12px 16px',
    textAlign: 'center',
    borderTop: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  viewAllLink: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  notificationItem: {
    display: 'flex',
    padding: '6px 10px',
    margin: '3px 8px',
    borderRadius: 8,
    alignItems: 'flex-start',
    gap: '8px',
    cursor: 'pointer',
    transition: 'opacity 0.2s',
    position: 'relative',
    '&:hover': {
      opacity: 0.9,
      '& $deleteButton': {
        opacity: 1,
      },
    },
  },
  deleteButton: {
    opacity: 0,
    transition: 'opacity 0.2s',
    padding: '4px',
    minWidth: 'auto',
    color: '#d32f2f',
    marginLeft: 'auto',
    flexShrink: 0,
    '&:hover': {
      backgroundColor: 'rgba(211, 47, 47, 0.1)',
    },
  },
  notificationItemInfo: {
    backgroundColor: '#e3f2fd',
  },
  notificationItemSuccess: {
    backgroundColor: '#e8f5e9',
  },
  notificationItemWarning: {
    backgroundColor: '#fff3e0',
  },
  notificationItemError: {
    backgroundColor: '#ffebee',
  },
  notificationItemRead: {
    backgroundColor: '#fafafa',
  },
  iconWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 36,
    width: 36,
    height: 36,
    borderRadius: '50%',
    flexShrink: 0,
    fontWeight: 600,
    fontSize: '14px',
    color: '#fff',
  },
  iconWrapperInfo: {
    backgroundColor: '#1976d2',
  },
  iconWrapperSuccess: {
    backgroundColor: '#388e3c',
  },
  iconWrapperWarning: {
    backgroundColor: '#f57c00',
  },
  iconWrapperError: {
    backgroundColor: '#c62828',
  },
  textContent: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontWeight: 600,
    fontSize: '13px',
    color: '#212121',
    marginBottom: '1px',
  },
  titleUnread: {
    fontWeight: 700,
  },
  senderName: {
    fontSize: '12px',
    color: '#666',
    marginBottom: '2px',
    fontWeight: 500,
  },
  message: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '6px',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  timestamp: {
    fontSize: '11px',
    color: '#999',
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
  },
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: '#ff5252',
    marginRight: '8px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    textAlign: 'center',
    color: '#999',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px',
  },
}));

const NotificationBell: React.FC = () => {
  const classes = useStyles();
  const intl = useIntl();
  const notificationService = useNotificationService();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [notifications, setNotifications] = useState<InAppNotificationDto[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [pollLoading, setPollLoading] = useState<boolean>(false);
  const [viewAll, setViewAll] = useState<boolean>(false);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchUnreadCount = async () => {
    try {
      setPollLoading(true);
      const response = await notificationService.getUnreadCount();
      setUnreadCount(typeof response === 'number' ? response : response.unreadCount || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    } finally {
      setPollLoading(false);
    }
  };

  const fetchUserNotifications = async () => {
    try {
      setLoading(true);
      const result = await notificationService.getUserNotifications(0, 50);
      console.log('Fetched notifications result:', result);
      const notifs = result.Values || [];
      console.log('Notifications to display:', notifs);
      notifs.forEach((n: any, idx: number) => {
        console.log(`Notification ${idx}:`, {
          id: n.id,
          title: n.title,
          message: n.message,
          notificationType: n.notificationType,
          createdByUserFirstName: n.createdByUserFirstName,
          createdByUserLastName: n.createdByUserLastName,
          createdByUserName: n.createdByUserName,
          createdOn: n.createdOn,
          isRead: n.isRead,
        });
      });
      setNotifications(notifs);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      createNotification.error(intl.formatMessage({ id: 'notifications.failedToLoad', defaultMessage: 'Failed to load notifications' }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Nudged slightly after mount so it doesn't compete with the current page's own
    // data fetch during the first-load burst of app-shell requests.
    const initialFetchTimer = setTimeout(fetchUnreadCount, 300);

    pollIntervalRef.current = setInterval(fetchUnreadCount, POLL_INTERVAL);

    return () => {
      clearTimeout(initialFetchTimer);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const handleBellClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
      fetchUserNotifications();
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setViewAll(false);
  };

  const handleMarkAsRead = async (notification: InAppNotificationDto) => {
    try {
      console.log('Marking as read:', notification.recipientId, notification);
      await notificationService.markAsRead(notification.recipientId);
      setNotifications((prev) =>
        prev.map((n) =>
          n.recipientId === notification.recipientId ? { ...n, isRead: true, readOn: new Date().toISOString() } : n
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
      createNotification.success(intl.formatMessage({ id: 'notifications.markedAsRead', defaultMessage: 'Notification marked as read' }));
    } catch (error) {
      console.error('Error marking notification as read:', error);
      createNotification.error(intl.formatMessage({ id: 'notifications.failedToMarkAsRead', defaultMessage: 'Failed to mark notification as read' }));
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
      createNotification.success(intl.formatMessage({ id: 'notifications.allMarkedAsRead', defaultMessage: 'All notifications marked as read' }));
    } catch (error) {
      console.error('Error marking all as read:', error);
      createNotification.error(intl.formatMessage({ id: 'notifications.failedToMarkAll', defaultMessage: 'Failed to mark all as read' }));
    }
  };

  const handleDelete = async (notification: InAppNotificationDto, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      console.log('Deleting notification:', notification.id, notification);
      if (!notification.id) {
        console.error('Notification ID is missing:', notification);
        createNotification.error(intl.formatMessage({ id: 'notifications.idMissing', defaultMessage: 'Notification ID missing' }));
        return;
      }
      await notificationService.delete(notification.id);
      setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
      setUnreadCount((prev) => Math.max(0, prev - 1));
      createNotification.success(intl.formatMessage({ id: 'notifications.deleted', defaultMessage: 'Notification deleted' }));
    } catch (error) {
      console.error('Error deleting notification:', error);
      createNotification.error(intl.formatMessage({ id: 'notifications.failedToDelete', defaultMessage: 'Failed to delete notification' }));
    }
  };

  const handleNotificationClick = (notification: InAppNotificationDto) => {
    if (!notification.isRead) {
      handleMarkAsRead(notification);
    }
    if (notification.linkUrl) {
      window.location.href = notification.linkUrl;
    }
  };

  const getIconComponent = (notificationType: number) => {
    switch (notificationType) {
      case 1:
        return CheckCircleIcon;
      case 2:
        return WarningIcon;
      case 3:
        return ErrorIcon;
      default:
        return InfoIcon;
    }
  };

  const getIconClassName = (notificationType: number) => {
    switch (notificationType) {
      case 1:
        return classes.iconWrapperSuccess;
      case 2:
        return classes.iconWrapperWarning;
      case 3:
        return classes.iconWrapperError;
      default:
        return classes.iconWrapperInfo;
    }
  };

  const getNotificationItemClassName = (notificationType: number) => {
    switch (notificationType) {
      case 1:
        return classes.notificationItemSuccess;
      case 2:
        return classes.notificationItemWarning;
      case 3:
        return classes.notificationItemError;
      default:
        return classes.notificationItemInfo;
    }
  };

  const formatTime = (dateString: string) => {
    if (!dateString) return intl.formatMessage({ id: 'notifications.unknownTime', defaultMessage: 'Unknown time' });

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return intl.formatMessage({ id: 'notifications.unknownTime', defaultMessage: 'Unknown time' });

    const now = new Date();
    const diffInSeconds = (now.getTime() - date.getTime()) / 1000;

    if (diffInSeconds < 0) return intl.formatMessage({ id: 'notifications.justNow', defaultMessage: 'Just now' });
    if (diffInSeconds < 60) return intl.formatMessage({ id: 'notifications.justNow', defaultMessage: 'Just now' });
    if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return intl.formatMessage({ id: 'notifications.minutesAgo', defaultMessage: '{count}m ago' }, { count: minutes });
    }
    if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return intl.formatMessage({ id: 'notifications.hoursAgo', defaultMessage: '{count}h ago' }, { count: hours });
    }
    const days = Math.floor(diffInSeconds / 86400);
    return intl.formatMessage({ id: 'notifications.daysAgo', defaultMessage: '{count}d ago' }, { count: days });
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <ClickAwayListener onClickAway={handleClose}>
        <div>
          <IconButton
            color="inherit"
            onClick={handleBellClick}
            aria-label="notifications"
            size="medium"
          >
            <Badge badgeContent={unreadCount} classes={{ badge: classes.badge }}>
              <NotificationsOutlinedIcon />
            </Badge>
          </IconButton>

          <Popover
            id={open ? 'notification-popover' : undefined}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            classes={{
              root: classes.popover,
              paper: classes.paper,
            }}
          >
        <Box className={classes.header}>
          <Typography className={classes.headerTitle}>
            {viewAll ? intl.formatMessage({ id: 'notifications.allNotifications', defaultMessage: 'All Notifications' }) : intl.formatMessage({ id: 'notifications.title', defaultMessage: 'Notifications' })}
          </Typography>
          {viewAll ? (
            <Button
              size="small"
              onClick={() => setViewAll(false)}
              className={classes.markAllBtn}
            >
              {intl.formatMessage({ id: 'back', defaultMessage: 'Back' })}
            </Button>
          ) : (
            unreadCount > 0 && (
              <Button
                size="small"
                onClick={handleMarkAllAsRead}
                className={classes.markAllBtn}
              >
                {intl.formatMessage({ id: 'notifications.markAllAsRead', defaultMessage: 'Mark all as read' })}
              </Button>
            )
          )}
        </Box>

        <Box className={classes.content}>
          {loading && (
            <Box className={classes.loadingContainer}>
              <CircularProgress size={30} />
            </Box>
          )}

          {!loading &&
            (viewAll ? notifications.length === 0 : notifications.filter((n) => !n.isRead).length === 0) && (
            <Box className={classes.emptyState}>
              <NotificationsOutlinedIcon style={{ fontSize: 40, marginBottom: 8 }} />
              <Typography variant="body2">
                {viewAll ? intl.formatMessage({ id: 'notifications.noNotifications', defaultMessage: 'No notifications' }) : intl.formatMessage({ id: 'notifications.noNotificationsToRead', defaultMessage: 'No notifications to read' })}
              </Typography>
            </Box>
          )}

          {!loading &&
            (viewAll ? notifications : notifications.filter((n) => !n.isRead)).map((notification) => {
              const senderName = notification.createdByUserFirstName && notification.createdByUserLastName
                ? `${notification.createdByUserFirstName} ${notification.createdByUserLastName}`
                : notification.createdByUserName || 'System';
              const senderInitial = senderName.charAt(0).toUpperCase();
              return (
                <Box
                  key={`${notification.id}-${notification.recipientId}`}
                  className={`${classes.notificationItem} ${getNotificationItemClassName(notification.notificationType)}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <Box className={`${classes.iconWrapper} ${getIconClassName(notification.notificationType)}`}>
                    {senderInitial}
                  </Box>

                  <Box className={classes.textContent}>
                    <Typography className={classes.senderName}>
                      {senderName}
                    </Typography>

                    <Box display="flex" alignItems="center" style={{ gap: '4px', marginBottom: '6px' }}>
                      {!notification.isRead && <Box className={classes.unreadDot} />}
                      <Typography
                        className={`${classes.title} ${!notification.isRead ? classes.titleUnread : ''}`}
                      >
                        {notification.title}
                      </Typography>
                    </Box>

                    <Typography className={classes.timestamp}>
                      <AccessTimeIcon style={{ fontSize: 12 }} />
                      {formatTime(notification.createdOn)}
                    </Typography>
                  </Box>

                  <IconButton
                    size="small"
                    className={classes.deleteButton}
                    onClick={(e) => handleDelete(notification, e)}
                    title={intl.formatMessage({ id: 'notifications.deleteNotification', defaultMessage: 'Delete notification' })}
                  >
                    <DeleteIcon style={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              );
            })}
        </Box>

        {!loading && !viewAll && (
          <Box className={classes.viewAllButton}>
            <Typography
              className={classes.viewAllLink}
              onClick={() => setViewAll(true)}
            >
              {intl.formatMessage({ id: 'notifications.viewAll', defaultMessage: 'View all notifications' })}
            </Typography>
          </Box>
        )}
          </Popover>
        </div>
      </ClickAwayListener>
    </>
  );
};

export default NotificationBell;
